import React, { Key } from "react";

import Vero1ErrorBoundary from "manager/components/vero1/Vero1ErrorBoundary";
import { Vero1ReviewTrigger } from "manager/components/vero1/Vero1ReviewPanel";
import {
  NO_OUTCOMES_MESSAGE,
  NO_OUTCOMES_TITLE,
} from "manager/components/vero1/Vero1ReviewPanel/generationStrings";
import {
  useGetVero1Checklist,
  useVero1ChecklistVersions,
} from "manager/hooks/api/vero1Queries";
import type { Vero1BannerState } from "shared/interfaces/api/vero1";
import {
  Vero1ChecklistStatus,
  Vero1GenerationStatus,
} from "shared/interfaces/api/vero1";

import { useVero1Viewer } from "../vero1Viewer";

import { LeasingReviewSection } from "./LeasingReviewSection";
import { LumenScreeningSection } from "./LumenScreeningSection";
import { reviewHoldNote } from "./reviewHold";
import { EmptyBody, EmptyState, EmptyTitle, SectionCard } from "./styled";
import { Vero1ReviewSection } from "./Vero1ReviewSection";

/* Spec §04 — two compositions, one tab. Everything drawn in 4.1–4.4 is the
   internal read; leasing gets 4.5: the VERO1 review section alone, latest
   submission only, no Lumen section, no selectors, no actions. Prior runs are
   the system's workings and stay internal; per-item change markers are
   VERO1's work and stay visible (8/17 stakeholder ruling; leasing scope per
   the finalized PRD 2026-08-21). */

export interface DealReviewTabProps {
  dealId: Key;
  vero1Active?: boolean;
  dealLabel?: string;
  latestLumenRun?: any;
  vero1BannerState?: Vero1BannerState | null;
}

const LeasingEmpty: React.FC<{ inProgress: boolean; replacing?: boolean }> = ({
  inProgress,
  replacing = false,
}) => (
  <SectionCard>
    <EmptyState data-testid="deal-review-empty">
      <EmptyTitle>
        {inProgress ? "VERO1 review in progress" : "No VERO1 review yet"}
      </EmptyTitle>
      <EmptyBody>
        {replacing
          ? "The VERO1 team is updating this deal's review. The earlier submission is on hold; the updated findings appear here once the new review is submitted."
          : "When the VERO1 review is complete, detailed findings appear here."}
      </EmptyBody>
    </EmptyState>
  </SectionCard>
);

const AgentEmpty: React.FC<{
  dealId: Key;
  vero1Active?: boolean;
  dealLabel?: string;
  draftOpen: boolean;
  noOutcomes: boolean;
  canAuthor: boolean;
}> = ({ dealId, vero1Active, dealLabel, draftOpen, noOutcomes, canAuthor }) =>
  noOutcomes && !draftOpen ? (
    <SectionCard>
      <EmptyState role="alert" data-testid="deal-review-no-outcomes">
        <EmptyTitle>{NO_OUTCOMES_TITLE}</EmptyTitle>
        <EmptyBody>{NO_OUTCOMES_MESSAGE}</EmptyBody>
      </EmptyState>
    </SectionCard>
  ) : (
    <SectionCard>
      <EmptyState data-testid="deal-review-empty">
        <EmptyTitle>
          {draftOpen ? "VERO1 review in progress" : "No VERO1 review yet"}
        </EmptyTitle>
        <EmptyBody>
          {canAuthor
            ? draftOpen
              ? "To view or edit, use the review panel. Once the review is submitted, detailed findings appear here."
              : "Start a VERO1 review. Lumen's results are the starting point."
            : draftOpen
            ? "A VERO1 agent is working on this review. Once it's submitted, detailed findings appear here."
            : "When the VERO1 review is complete, detailed findings appear here."}
        </EmptyBody>
        {/* the trigger owns generation and renders the verb for the draft state */}
        {canAuthor && (
          <Vero1ReviewTrigger
            dealId={dealId}
            vero1Active={vero1Active}
            dealLabel={dealLabel}
          />
        )}
      </EmptyState>
    </SectionCard>
  );

const DealReviewTabInner: React.FC<DealReviewTabProps> = ({
  dealId,
  vero1Active,
  dealLabel,
  latestLumenRun,
  vero1BannerState,
}) => {
  const { canViewInternal, canAuthor } = useVero1Viewer();
  const holdNote = reviewHoldNote(vero1BannerState, canViewInternal);
  const {
    checklist,
    reviewInProgress,
    rereview,
    generation,
    isChecklistLoading,
  } = useGetVero1Checklist(
    dealId,
    Boolean(vero1Active),
    /* pollWhileOpen */ false
  );
  const { versions, versionsLoading } = useVero1ChecklistVersions(
    dealId,
    Boolean(vero1Active) && canViewInternal
  );

  if (!vero1Active) return null;
  // no flash-of-empty-state while the first read is in flight. The agent view
  // decides empty vs not on `versions`, which is a SEPARATE query — waiting on
  // the checklist alone let "no review yet" paint before the version list
  // landed, then swap once it did.
  if (isChecklistLoading && !checklist) return null;
  if (canViewInternal && versionsLoading && versions.length === 0) return null;

  if (!canViewInternal) {
    // leasing: the client payload IS the latest submitted review (drafts are
    // excluded server-side). A review a later run superseded is still that
    // submission — it stays readable with the hold named, never "in progress"
    const submitted =
      checklist?.status === Vero1ChecklistStatus.Submitted ||
      checklist?.status === Vero1ChecklistStatus.NeedsReReview
        ? checklist
        : null;
    // a restart draft parks the record: leasing keeps the tab, not a verdict
    // that is about to be replaced
    if (submitted && reviewInProgress) {
      return <LeasingEmpty inProgress replacing />;
    }
    return submitted ? (
      <LeasingReviewSection checklist={submitted} holdNote={holdNote} />
    ) : (
      <LeasingEmpty inProgress={reviewInProgress || Boolean(checklist)} />
    );
  }

  const draft =
    checklist?.status === Vero1ChecklistStatus.Draft ? checklist : null;
  const draftOpen = reviewInProgress || Boolean(draft);

  return (
    <div data-testid="deal-review-tab">
      {versions.length > 0 || draft ? (
        <Vero1ReviewSection
          dealId={dealId}
          versions={versions}
          rereview={rereview}
          dealLabel={dealLabel}
          vero1Active={vero1Active}
          draftOpen={draftOpen}
          draft={draft}
          canAuthor={canAuthor}
        />
      ) : (
        <AgentEmpty
          dealId={dealId}
          vero1Active={vero1Active}
          dealLabel={dealLabel}
          draftOpen={draftOpen}
          noOutcomes={generation?.state === Vero1GenerationStatus.NoOutcomes}
          canAuthor={canAuthor}
        />
      )}
      <LumenScreeningSection
        dealId={dealId}
        latestLumenRun={latestLumenRun}
        vero1Active={vero1Active}
        latestSubmittedAt={
          versions
            .filter((v) => v.status === Vero1ChecklistStatus.Submitted)
            .map((v) => v.submittedAt)
            .filter((t): t is string => Boolean(t))
            .sort()
            .pop() ?? null
        }
      />
    </div>
  );
};

export const DealReviewTab: React.FC<DealReviewTabProps> = (props) => {
  const { dealId } = props;
  return (
    <Vero1ErrorBoundary context="vero1_deal_review_tab" dealId={dealId} framed>
      <DealReviewTabInner {...props} />
    </Vero1ErrorBoundary>
  );
};
